/**
 * Admin Boundary Service — fetches curated geoBoundaries data from R2.
 *
 * Replaces the Overture Maps PMTiles approach with 207 countries of named,
 * population-enriched boundary polygons served from Cloudflare R2.
 *
 * Data sources:
 *   admin_metadata.json   — 232 countries, ADM0–ADM5 local terms, drill depths
 *   synonym_dictionary.json — 43 global + 958 country-specific natural language mappings
 *   R2 manifest.json       — inventory of available boundary files per country
 *   R2 GeoJSON files       — {ISO}_{ADMn}_pop.geojson with shapeName, population, area, density
 */

import { ISO2_TO_ISO3 } from '../utils/iso-codes.js';
import { deserialize as fgbDeserialize } from 'flatgeobuf/lib/mjs/geojson.js';
import { catalog } from './catalog.js';
import { R2_BASE_URL } from '../config.js';

const V1_R2_BASE = R2_BASE_URL;

/**
 * Normalize a string for fuzzy matching: strip diacritics, lowercase, trim.
 * e.g. "São Paulo" → "sao paulo", "Île-de-France" → "ile-de-france"
 */
function normalizeForMatch(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

// Whole-level high-res GeoJSON cap (CL-234). Mirrors the choropleth path's
// MAX_HIRES_CHOROPLETH_FEATURES in ops/boundaries.js: above this, whole-level
// rendering belongs to PMTiles, not a GeoJSON pull.
const HIGHRES_LEVEL_MAX_FEATURES = 15000;

class AdminBoundaryService {
  constructor() {
    this._metadata = null;      // admin_metadata.json: ISO3 → { name, continent, levels, maxDepth }
    this._synonyms = null;      // synonym_dictionary.json: { global, perCountry }
    this._manifest = null;      // R2 manifest.json: { metadata, countries }
    this._cache = new Map();    // Map<'USA_ADM1', GeoJSON FeatureCollection>
    this._nameIndex = null;     // Map<lowercase name, ISO3> for country name resolution
    this._ready = false;
    this._drillState = null;    // { iso3, currentLevel: 'ADM1' } — tracks current drill position
  }

  get isReady() { return this._ready; }

  /**
   * Load metadata files. Call during app init (non-blocking, after globe loads).
   */
  /**
   * Initialize the service (idempotent). Repeated calls — including an op that
   * awaits readiness before resolving a country — share the one in-flight
   * promise instead of re-fetching. Resolves once metadata + name index are
   * ready. (CL-116: region resolution used to fail for queries fired before
   * this async load finished, since getCountry returns null until _metadata is
   * set.)
   */
  init() {
    if (this._initPromise) return this._initPromise;
    this._initPromise = this._doInit();
    return this._initPromise;
  }

  async _doInit() {
    try {
      // Cache-bust local JSON files to avoid stale Service Worker responses
      const cacheBust = `?v=${Date.now()}`;
      const [metadata, synonyms, manifest] = await Promise.all([
        fetch(`/data/admin/admin_metadata.json${cacheBust}`).then(r => r.json()),
        fetch(`/data/admin/synonym_dictionary.json${cacheBust}`).then(r => r.json()),
        fetch(`${V1_R2_BASE}/geoboundaries_data/population/manifest.json`).then(r => r.json()),
      ]);

      this._metadata = metadata;
      this._synonyms = synonyms;
      this._manifest = manifest;

      // Build name → ISO3 index for fast country resolution
      this._nameIndex = new Map();
      for (const [iso3, entry] of Object.entries(metadata)) {
        this._nameIndex.set(entry.name.toLowerCase(), iso3);
        this._nameIndex.set(iso3.toLowerCase(), iso3);
      }

      // Add ISO-2 → ISO-3 entries so 2-letter codes resolve correctly
      for (const [iso2, iso3] of Object.entries(ISO2_TO_ISO3)) {
        if (this._metadata[iso3]) {
          this._nameIndex.set(iso2.toLowerCase(), iso3);
        }
      }

      this._ready = true;
      const countryCount = Object.keys(metadata).length;
      const manifestCount = manifest?.metadata?.total_countries || 0;
      console.log(`[AdminBoundaries] Ready: ${countryCount} countries in metadata, ${manifestCount} with boundary data`);
    } catch (err) {
      console.warn('[AdminBoundaries] Init failed:', err.message);
    }
  }

  /**
   * Resolve a country name, ISO-3, or ISO-2 code to the metadata entry.
   * Returns { iso3, name, continent, levels, maxDepth, drillableLevels } or null.
   */
  getCountry(nameOrIso) {
    if (!this._metadata || !nameOrIso) return null;
    const query = nameOrIso.trim();

    // Direct ISO-3 lookup
    const upper = query.toUpperCase();
    if (this._metadata[upper]) {
      return { iso3: upper, ...this._metadata[upper] };
    }

    // Name index (case-insensitive), strip "the " prefix
    const queryLower = query.toLowerCase().replace(/^the\s+/, '');
    const iso3 = this._nameIndex?.get(queryLower) || this._nameIndex?.get(query.toLowerCase());
    if (iso3 && this._metadata[iso3]) {
      return { iso3, ...this._metadata[iso3] };
    }

    // Partial match: "United States" → "United States of America"
    // Strip "the " prefix and require min key length of 4 to avoid ISO code false positives
    const lower = query.toLowerCase().replace(/^the\s+/, '');
    for (const [key, iso] of this._nameIndex.entries()) {
      if (key.length < 4) continue; // skip short keys (ISO codes) in fuzzy pass
      if (key.startsWith(lower) || lower.startsWith(key)) {
        if (this._metadata[iso]) {
          return { iso3: iso, ...this._metadata[iso] };
        }
      }
    }

    return null;
  }

  /**
   * Resolve a natural language admin term to an ADM level for a specific country.
   * e.g. resolveAdminLevel('IND', 'states') → 'ADM1'
   * e.g. resolveAdminLevel('FRA', 'departments') → 'ADM2'
   * Supports special values: 'MAX_DEPTH', 'DRILL_DOWN', 'DRILL_UP'
   */
  resolveAdminLevel(iso3, term) {
    if (!this._synonyms || !term) return null;
    const normalizedTerm = term.toLowerCase().trim();

    // Handle MAX_DEPTH: return the deepest available level with data
    if (normalizedTerm === 'max_depth' || normalizedTerm === 'most detailed' || normalizedTerm === 'lowest level') {
      return this.getDeepestLevel(iso3);
    }

    // Handle DRILL_DOWN: go one level deeper from current drill state
    if (normalizedTerm === 'drill_down') {
      return this.drillDown(iso3);
    }

    // Handle DRILL_UP: go one level shallower
    if (normalizedTerm === 'drill_up') {
      return this.drillUp(iso3);
    }

    // 1. Country-specific synonyms (highest priority)
    const countryEntry = this._synonyms.perCountry?.[iso3];
    if (countryEntry) {
      const level = countryEntry[normalizedTerm];
      if (level) return this._resolveAmbiguousLevel(iso3, level);
    }

    // 2. Global synonyms
    const globalLevel = this._synonyms.global?.[normalizedTerm];
    if (globalLevel) return this._resolveAmbiguousLevel(iso3, globalLevel);

    // 3. Direct ADM level reference ("ADM1", "level 1", etc.)
    const admMatch = normalizedTerm.match(/^adm(\d)$/i);
    if (admMatch) return `ADM${admMatch[1]}`;

    return null;
  }

  /**
   * Get the deepest admin level that has data on R2 for a country.
   */
  getDeepestLevel(iso3) {
    for (const level of ['ADM5', 'ADM4', 'ADM3', 'ADM2', 'ADM1']) {
      if (this.hasData(iso3, level)) return level;
    }
    return 'ADM1';
  }

  /**
   * Drill down one level deeper. Tracks state per country.
   */
  drillDown(iso3) {
    const currentLevel = this._drillState?.iso3 === iso3
      ? parseInt(this._drillState.currentLevel.replace('ADM', ''))
      : 1;
    const nextLevel = currentLevel + 1;
    const nextAdm = `ADM${nextLevel}`;
    if (this.hasData(iso3, nextAdm)) {
      this._drillState = { iso3, currentLevel: nextAdm };
      return nextAdm;
    }
    // Already at deepest level
    return this._drillState?.currentLevel || `ADM${currentLevel}`;
  }

  /**
   * Drill up one level shallower. Tracks state per country.
   */
  drillUp(iso3) {
    const currentLevel = this._drillState?.iso3 === iso3
      ? parseInt(this._drillState.currentLevel.replace('ADM', ''))
      : 1;
    const prevLevel = Math.max(1, currentLevel - 1);
    const prevAdm = `ADM${prevLevel}`;
    this._drillState = { iso3, currentLevel: prevAdm };
    return prevAdm;
  }

  /**
   * Set the current drill state (called when boundaries are displayed).
   */
  setDrillState(iso3, admLevel) {
    this._drillState = { iso3, currentLevel: admLevel };
  }

  /**
   * Resolve ambiguous levels like "ADM1_OR_ADM2" based on what the country actually has.
   */
  _resolveAmbiguousLevel(iso3, level) {
    if (!level.includes('_OR_')) return level;

    // e.g. "ADM1_OR_ADM2" → try ADM1 first, then ADM2
    const options = level.split('_OR_');
    const countryMeta = this._metadata[iso3];
    if (!countryMeta) return options[0];

    for (const opt of options) {
      if (countryMeta.levels[opt]) return opt;
    }
    return options[0];
  }

  /**
   * Get the local terminology for an admin level.
   * e.g. getLocalTerm('IND', 'ADM1') → { singular: 'State / Union Territory', plural: 'States / Union Territories' }
   */
  getLocalTerm(iso3, admLevel) {
    const entry = this._metadata?.[iso3]?.levels?.[admLevel];
    if (!entry) return null;
    return { singular: entry.localTerm, plural: entry.plural };
  }

  /**
   * Check if boundary data exists on R2 for a country + admin level.
   */
  hasData(iso3, admLevel) {
    return !!this._manifest?.countries?.[iso3]?.levels?.[admLevel];
  }

  /**
   * Get the max drill depth for a country.
   */
  getMaxDepth(iso3) {
    return this._metadata?.[iso3]?.maxDepth || 0;
  }

  /**
   * Get all admin levels for a country.
   * Returns [{ level: 'ADM1', term: 'State', plural: 'States', hasData: true }, ...]
   */
  getAdminHierarchy(iso3) {
    const meta = this._metadata?.[iso3];
    if (!meta) return [];

    return Object.entries(meta.levels).map(([level, info]) => ({
      level,
      term: info.localTerm,
      plural: info.plural,
      hasData: this.hasData(iso3, level),
    }));
  }

  /**
   * Get manifest info for a country + admin level (feature count, population totals).
   */
  getManifestInfo(iso3, admLevel) {
    return this._manifest?.countries?.[iso3]?.levels?.[admLevel] || null;
  }

  /**
   * Fetch boundary data for a country + admin level.
   * Tries FlatGeobuf (HTTP range requests) first for large datasets,
   * falls back to full GeoJSON download.
   *
   * @param {string} iso3 - Country ISO-3 code
   * @param {string} admLevel - Admin level (ADM1, ADM2, etc.)
   * @param {object} bbox - Optional bounding box { minX, minY, maxX, maxY } for FlatGeobuf spatial filter
   * @returns {object|null} GeoJSON FeatureCollection
   */
  async getBoundaries(iso3, admLevel, bbox) {
    const cacheKey = `${iso3}_${admLevel}`;
    if (!bbox && this._cache.has(cacheKey)) return this._cache.get(cacheKey);

    if (!this.hasData(iso3, admLevel)) {
      console.warn(`[AdminBoundaries] No data for ${iso3} ${admLevel}`);
      return null;
    }

    // HIGH-RES FIRST (CL-234): serve the whole level from the boundary parquet
    // (Overture ADM0-2 + geoBoundaries High-Precision ADM3-5) whenever it can
    // fully substitute for the legacy simplified polygons. This is the single
    // chokepoint every whole-level consumer flows through — outlines,
    // drill-downs, territory POIs, centroid enrichment, division choropleths —
    // so upgrading here upgrades them all. Gates (all fail soft to legacy):
    //  - parquet enabled + present for the country (230/232, PSE/XKX excepted);
    //  - ATTRIBUTED per the A3b backfill — many consumers read population/
    //    density/area off the features, and the raw parquet is geometry-only;
    //  - known feature count ≤ HIGHRES_LEVEL_MAX_FEATURES (huge levels like
    //    India villages stay on PMTiles/legacy — a whole-level GeoJSON pull
    //    at that scale is the wrong tool regardless of source).
    let hiresEligible = false;
    if (!bbox) {
      const attempt = await this._getHighResLevel(iso3, admLevel);
      hiresEligible = attempt.eligible;
      if (attempt.fc) {
        this._cache.set(cacheKey, attempt.fc);
        return attempt.fc;
      }
    }

    // FlatGeobuf disabled — .fgb files not yet uploaded to R2.
    // When available, re-enable: try _fetchFlatGeobuf(fgbUrl, bbox) with GeoJSON fallback.

    // Legacy fallback: full simplified-GeoJSON download
    const url = `${V1_R2_BASE}/geoboundaries_data/population/${iso3}/${iso3}_${admLevel}_pop.geojson`;

    try {
      console.log(`[AdminBoundaries] Fetching GeoJSON ${iso3} ${admLevel}...`);
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`[AdminBoundaries] Fetch failed: ${response.status} for ${url}`);
        return null;
      }

      const geojson = await response.json();
      // A TRANSIENT high-res failure (cold DuckDB on the first query of a
      // session, a flaky read) must not pin the legacy shapes for the whole
      // session — skip the cache when high-res was eligible but failed, so
      // the next request retries the parquet (CL-235).
      if (!hiresEligible) this._cache.set(cacheKey, geojson);
      console.log(`[AdminBoundaries] Loaded ${iso3} ${admLevel}: ${geojson.features?.length || 0} features`);
      return geojson;
    } catch (err) {
      console.warn(`[AdminBoundaries] Fetch error for ${iso3} ${admLevel}:`, err.message);
      return null;
    }
  }

  /**
   * Try the high-res boundary parquet for a WHOLE admin level (CL-234).
   * Returns an attribute-bearing FeatureCollection or null. Never throws —
   * every failure (module load, DuckDB cold, catalog gaps, query error) falls
   * back to the legacy source in getBoundaries().
   */
  async _getHighResLevel(iso3, admLevel) {
    // Returns { eligible, fc }: `eligible` means every static gate passed, so
    // a null fc is a RUNTIME failure (cold DuckDB, flaky read) — the caller
    // then avoids caching the legacy fallback, keeping the retry path open.
    try {
      const [{ boundaryParquetEnabled, hasBoundaryParquet, getDivisionLevel }, { catalog }] =
        await Promise.all([import('./boundary-feature-service.js'), import('./catalog.js')]);
      if (!boundaryParquetEnabled() || !hasBoundaryParquet(iso3)) return { eligible: false, fc: null };
      if (!catalog?.geoparquetAttributed?.(iso3, admLevel)) return { eligible: false, fc: null };
      const count = catalog.getFeatureCount?.(iso3, admLevel) || 0;
      if (!count || count > HIGHRES_LEVEL_MAX_FEATURES) return { eligible: false, fc: null };
      const fc = await getDivisionLevel(iso3, admLevel);
      if (!fc?.features?.length) return { eligible: true, fc: null };
      console.log(
        `[AdminBoundaries] High-res parquet level ${iso3} ${admLevel}: ${fc.features.length} features`
      );
      return { eligible: true, fc };
    } catch (err) {
      console.warn(`[AdminBoundaries] High-res level failed for ${iso3} ${admLevel}:`, err.message);
      return { eligible: true, fc: null };
    }
  }

  /**
   * Fetch features from a FlatGeobuf file using HTTP range requests.
   * Supports optional bounding box for spatial filtering.
   */
  async _fetchFlatGeobuf(url, bbox) {
    const rect = bbox ? { minX: bbox.minX, minY: bbox.minY, maxX: bbox.maxX, maxY: bbox.maxY } : undefined;

    // Check if the .fgb file exists with a HEAD request
    const head = await fetch(url, { method: 'HEAD' });
    if (!head.ok) return null;

    const features = [];
    const iter = fgbDeserialize(url, rect);
    for await (const feature of iter) {
      features.push(feature);
    }

    return { type: 'FeatureCollection', features };
  }

  /**
   * Find a division feature by name within a country's admin level.
   * Uses fuzzy matching: exact → case-insensitive → startsWith → includes.
   * Returns the matched GeoJSON feature or null.
   */
  findDivision(geojson, name) {
    if (!geojson?.features?.length || !name) return null;
    const q = normalizeForMatch(name);

    // Pass 1: Exact match on name_en/shapeName or iso3/shapeISO (diacritic-insensitive)
    for (const f of geojson.features) {
      const sn = normalizeForMatch(f.properties.name_en || f.properties.shapeName || '');
      const si = normalizeForMatch(f.properties.iso3 || f.properties.shapeISO || '');
      if (sn === q || si === q) return f;
    }

    // Pass 2: name starts with query or query starts with name
    for (const f of geojson.features) {
      const sn = normalizeForMatch(f.properties.name_en || f.properties.shapeName || '');
      if (sn.length >= 3 && (sn.startsWith(q) || q.startsWith(sn))) return f;
    }

    // Pass 3: query contained in name (e.g., "Ramsey" matches "Ramsey County")
    for (const f of geojson.features) {
      const sn = normalizeForMatch(f.properties.name_en || f.properties.shapeName || '');
      if (sn.includes(q) || q.includes(sn)) return f;
    }

    return null;
  }

  /**
   * Enrich a high-res division feature (from the Overture GeoParquet, which is
   * geometry-only) with authoritative population/area/density.
   *
   * The high-res parquet carries Overture English names ("Bavaria") and an
   * unreliable area_km2, whereas admin_summary / the legacy *_pop.geojson use
   * local geoBoundaries names ("Bayern") — so name- and area-based joins both
   * fail (measured: 8/16 ADM1, 12/399 ADM2 for DEU). We bridge geometrically:
   * find the legacy population feature that CONTAINS the high-res centroid and
   * read its attributes. Name-independent and robust.
   *
   * @returns {Promise<{population:number, area_km2:number, density_per_km2:number,
   *   pop_density_km2:number, pop_pct_national:number}|null>} enrichment props, or null.
   */
  async enrichDivisionByCentroid(iso3, admLevel, feature) {
    if (!feature?.geometry || !iso3 || !admLevel) return null;
    const centroid = _computeFeatureCentroid(feature);
    if (!centroid) return null;

    let geojson;
    try {
      geojson = await this.getBoundaries(iso3, admLevel);
    } catch {
      return null;
    }
    if (!geojson?.features?.length) return null;

    const hit = geojson.features.find((f) => _pointInFeature(centroid[0], centroid[1], f));
    if (!hit) return null;

    const p = hit.properties || {};
    const population = Number(p.population);
    const area = Number(p.area_km2 ?? p.area);
    const density = Number(p.density_per_km2 ?? p.pop_density_km2);
    const pctNational = Number(p.pop_pct_national);
    const out = {};
    if (Number.isFinite(population)) out.population = population;
    if (Number.isFinite(area)) out.area_km2 = area;
    if (Number.isFinite(density)) {
      out.density_per_km2 = density;
      out.pop_density_km2 = density;
    }
    if (Number.isFinite(pctNational)) out.pop_pct_national = pctNational;
    return Object.keys(out).length ? out : null;
  }

  /**
   * Find a division feature using parent context for disambiguation.
   * e.g., findDivisionInParent('USA', 'ADM2', 'Ramsey', 'Minnesota')
   * Uses centroid-in-polygon to verify the child is inside the parent.
   */
  async findDivisionInParent(iso3, childLevel, childName, parentName) {
    // Fetch child boundaries
    const childGeoJSON = await this.getBoundaries(iso3, childLevel);
    if (!childGeoJSON?.features?.length || !childName) return null;
    const q = normalizeForMatch(childName);

    // Find ALL candidate features matching the name (diacritic-insensitive)
    const candidates = childGeoJSON.features.filter(f => {
      const sn = normalizeForMatch(f.properties.name_en || f.properties.shapeName || '');
      return sn === q || sn.includes(q) || q.includes(sn);
    });

    if (candidates.length === 0) return null;
    if (candidates.length === 1) return candidates[0];

    // Multiple matches — disambiguate using parent polygon
    if (!parentName) return candidates[0]; // No parent context, return first

    // Determine parent level (one level up from child)
    const parentLevel = childLevel === 'ADM2' ? 'ADM1'
      : childLevel === 'ADM3' ? 'ADM2'
      : childLevel === 'ADM4' ? 'ADM3'
      : childLevel === 'ADM5' ? 'ADM4'
      : null;
    if (!parentLevel) return candidates[0];

    const parentGeoJSON = await this.getBoundaries(iso3, parentLevel);
    if (!parentGeoJSON?.features?.length) return candidates[0];

    // Find the parent feature
    const parentFeature = this.findDivision(parentGeoJSON, parentName);
    if (!parentFeature) return candidates[0];

    // Compute centroids of each candidate and check which falls inside parent
    for (const candidate of candidates) {
      const centroid = _computeFeatureCentroid(candidate);
      if (centroid && _pointInFeature(centroid[0], centroid[1], parentFeature)) {
        return candidate;
      }
    }

    return candidates[0]; // Fallback: first match if spatial check fails
  }

  /**
   * Get the default admin level for a country (the first level with data).
   */
  /**
   * Check if a country+level should use PMTiles.
   * Uses catalog format data when available, falls back to manifest check.
   */
  shouldUsePMTiles(iso3, admLevel) {
    // Test override: force the PMTiles path for pilot countries so the new
    // Overture+geoBoundaries-HP tiles render even at small levels. Set
    // VITE_PMTILES_TEST_COUNTRIES (e.g. "USA,FRA,IND") together with
    // VITE_PMTILES_BASE. Unset → normal behaviour, production unaffected.
    const testCountries = (import.meta.env?.VITE_PMTILES_TEST_COUNTRIES || '')
      .split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
    if (testCountries.includes(iso3)) return true;

    // Prefer catalog-driven check (knows exact format availability)
    if (catalog.isReady) {
      return catalog.shouldUsePMTiles(iso3, admLevel);
    }
    // Fallback: manifest-based check
    const info = this.getManifestInfo(iso3, admLevel);
    if (!info) return false;
    const hasPMTiles = !!this._manifest?.countries?.[iso3]?.pmtiles_available;
    return info.feature_count >= 2000 && hasPMTiles;
  }

  getDefaultLevel(iso3) {
    const meta = this._metadata?.[iso3];
    if (!meta) return null;
    for (const level of ['ADM1', 'ADM2', 'ADM3', 'ADM4', 'ADM5']) {
      if (meta.levels[level] && this.hasData(iso3, level)) return level;
    }
    return null;
  }

  /**
   * Filter child-level features to only those within a named parent division.
   * e.g. filter USA ADM2 (all 3,233 counties) to just those inside "Texas" (ADM1).
   *
   * @param {GeoJSON.FeatureCollection} childGeojson - Full child-level GeoJSON
   * @param {string} iso3 - Country ISO3 code
   * @param {string} childLevel - e.g. 'ADM2'
   * @param {string} parentDivisionName - e.g. 'Texas', 'California'
   * @returns {Promise<GeoJSON.FeatureCollection>} Filtered FeatureCollection (or original if filtering fails)
   */
  async filterByParentDivision(childGeojson, iso3, childLevel, parentDivisionName) {
    if (!childGeojson?.features?.length || !parentDivisionName) return childGeojson;

    // Determine parent admin level (one level up)
    const parentLevel = childLevel === 'ADM2' ? 'ADM1'
      : childLevel === 'ADM3' ? 'ADM2'
      : childLevel === 'ADM4' ? 'ADM3'
      : childLevel === 'ADM5' ? 'ADM4'
      : null;
    if (!parentLevel) return childGeojson;

    try {
      const parentGeoJSON = await this.getBoundaries(iso3, parentLevel);
      if (!parentGeoJSON?.features?.length) return childGeojson;

      // Find the parent feature by name
      const parentFeature = this.findDivision(parentGeoJSON, parentDivisionName);
      if (!parentFeature) {
        console.warn(`[AdminBoundaries] filterByParentDivision: could not find "${parentDivisionName}" in ${iso3} ${parentLevel}`);
        return childGeojson;
      }

      // Filter child features: keep those whose centroid falls inside the parent polygon
      const filtered = childGeojson.features.filter(f => {
        const centroid = _computeFeatureCentroid(f);
        return centroid && _pointInFeature(centroid[0], centroid[1], parentFeature);
      });

      console.log(`[AdminBoundaries] filterByParentDivision: ${filtered.length}/${childGeojson.features.length} features inside "${parentDivisionName}"`);

      if (filtered.length === 0) {
        console.warn(`[AdminBoundaries] filterByParentDivision: no features found inside "${parentDivisionName}", returning all`);
        return childGeojson;
      }

      return { type: 'FeatureCollection', features: filtered };
    } catch (err) {
      console.warn(`[AdminBoundaries] filterByParentDivision failed:`, err.message);
      return childGeojson;
    }
  }
}

/**
 * Compute the centroid of a GeoJSON feature's geometry.
 */
function _computeFeatureCentroid(feature) {
  const coords = _extractCoords(feature.geometry);
  if (coords.length === 0) return null;
  let sumLng = 0;
  let sumLat = 0;
  for (const [lng, lat] of coords) {
    sumLng += lng;
    sumLat += lat;
  }
  return [sumLng / coords.length, sumLat / coords.length];
}

function _extractCoords(geometry) {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return geometry.coordinates.flat();
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat(2);
  return [];
}

/**
 * Check if a point falls inside a GeoJSON feature (Polygon or MultiPolygon).
 */
function _pointInFeature(lng, lat, feature) {
  const geom = feature.geometry;
  if (geom.type === 'Polygon') return _pointInRing(lng, lat, geom.coordinates[0]);
  if (geom.type === 'MultiPolygon') {
    for (const polygon of geom.coordinates) {
      if (_pointInRing(lng, lat, polygon[0])) return true;
    }
  }
  return false;
}

function _pointInRing(lng, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersect = ((yi > lat) !== (yj > lat)) &&
      (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export const adminBoundaries = new AdminBoundaryService();
